import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ExcelTemplateGenerator from './ExcelTemplateGenerator';
import Papa from 'papaparse';

const { FiX, FiUpload, FiDownload, FiAlertCircle, FiCheckCircle, FiFile, FiFileText, FiLoader, FiTrash2, FiRefreshCw } = FiIcons;

const ExcelImporter = ({ onClose, onImport, countries }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [importedData, setImportedData] = useState(null);
  const [showTemplateGenerator, setShowTemplateGenerator] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [willOverride, setWillOverride] = useState(true); // Default to override mode
  const [processingStatus, setProcessingStatus] = useState('');
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && isValidFile(droppedFile)) {
      setFile(droppedFile);
      processFile(droppedFile);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && isValidFile(selectedFile)) {
      setFile(selectedFile);
      processFile(selectedFile);
    }
  };

  const isValidFile = (file) => {
    const fileName = file.name.toLowerCase();
    const isCSV = fileName.endsWith('.csv');
    const isJSON = fileName.endsWith('.json');
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    
    if (!isCSV && !isJSON && !isExcel) {
      setError('Please upload a valid Excel, CSV, or JSON file');
      return false;
    }
    setError(null);
    return true;
  };

  // Enhanced function to clean and validate URLs
  const cleanAndValidateUrl = (url) => {
    if (!url) return '';
    
    let cleanUrl = url.trim();
    
    // Check if URL has protocol, if not add it
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }
    
    try {
      const urlObj = new URL(cleanUrl);
      return urlObj.href;
    } catch (e) {
      // If it's not a valid URL, try to extract URL parts
      const urlMatch = cleanUrl.match(/(https?:\/\/[^\s]+)/);
      if (urlMatch && urlMatch[0]) {
        try {
          const extractedUrl = new URL(urlMatch[0]);
          return extractedUrl.href;
        } catch (e) {
          return '';
        }
      }
      return '';
    }
  };

  // Function to fetch favicon from a website
  const fetchWebsiteData = async (url) => {
    if (!url) return { logoImage: '' };
    
    try {
      const cleanUrl = cleanAndValidateUrl(url);
      if (!cleanUrl) return { logoImage: '' };
      
      const urlObj = new URL(cleanUrl);
      const domain = urlObj.hostname;
      
      // Get favicon using Google's favicon service
      const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
      
      // Verify the logo image is accessible
      const logoSuccess = await new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = faviconUrl;
      });
      
      return {
        logoImage: logoSuccess ? faviconUrl : ''
      };
    } catch (error) {
      console.error('Error fetching website data:', error);
      return { logoImage: '' };
    }
  };

  const processFile = async (file) => {
    setIsLoading(true);
    setError(null);
    setPreviewData(null);
    setProcessingStatus('Reading file...');
    
    try {
      const fileName = file.name.toLowerCase();
      
      // For CSV files
      if (fileName.endsWith('.csv')) {
        const text = await file.text();
        Papa.parse(text, {
          header: true,
          complete: (results) => {
            if (results.errors && results.errors.length > 0) {
              setError(`CSV parsing error: ${results.errors[0].message}`);
              setIsLoading(false);
              return;
            }
            
            const data = processImportedRows(results.data);
            setImportedData(data);
            // Generate preview data
            generatePreviewData(data);
            setIsLoading(false);
          },
          error: (error) => {
            setError(`CSV parsing error: ${error.message}`);
            setIsLoading(false);
          }
        });
      }
      // For JSON files
      else if (fileName.endsWith('.json')) {
        const text = await file.text();
        try {
          const jsonData = JSON.parse(text);
          
          // Check if the JSON has the expected structure
          if (typeof jsonData !== 'object') {
            throw new Error('Invalid JSON format. Expected an object with country codes as keys');
          }
          
          // Validate and process the JSON data
          const data = {};
          Object.keys(jsonData).forEach(countryCode => {
            const upperCountryCode = countryCode.toUpperCase();
            if (!countries.some(c => c.code === upperCountryCode)) return; // Skip invalid countries
            
            if (!Array.isArray(jsonData[countryCode])) {
              throw new Error(`Invalid data for country ${countryCode}. Expected an array of coupons`);
            }
            
            data[upperCountryCode] = jsonData[countryCode].map(coupon => ({
              brand: coupon.brand || 'Unknown Brand',
              title: coupon.title || 'Special Offer',
              description: coupon.description || 'Limited time offer',
              discount: coupon.discount || '10%',
              code: coupon.code || `CODE${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
              logoImage: coupon.logoImage || '',
              storeUrl: cleanAndValidateUrl(coupon.storeUrl) || '',
            }));
          });
          
          setImportedData(data);
          // Generate preview data
          generatePreviewData(data);
          
        } catch (err) {
          setError(`JSON parsing error: ${err.message}`);
        }
        setIsLoading(false);
      }
      // For Excel files
      else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        try {
          const arrayBuffer = await file.arrayBuffer();
          setProcessingStatus('Processing Excel file...');
          // Dynamically import xlsx only when needed
          const XLSX = await import('xlsx');
          
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet);
          
          if (rows.length === 0) {
            setError('Excel file appears to be empty');
            setIsLoading(false);
            return;
          }
          
          const data = processImportedRows(rows);
          setImportedData(data);
          // Generate preview data
          generatePreviewData(data);
          
        } catch (err) {
          console.error('Excel processing error:', err);
          setError(`Excel processing error: ${err.message}`);
        }
        setIsLoading(false);
      }
      
    } catch (err) {
      console.error('Error processing file:', err);
      setError(`Error processing file: ${err.message}`);
      setImportedData(null);
      setIsLoading(false);
    } finally {
      setProcessingStatus('');
    }
  };

  const processImportedRows = (rows) => {
    const data = {};
    
    rows.forEach(row => {
      const countryCode = (row.country_code || row.country || '').toUpperCase();
      if (!countryCode || !countries.some(c => c.code === countryCode)) return; // Skip invalid countries
      
      if (!data[countryCode]) {
        data[countryCode] = [];
      }
      
      // Extract and clean store URL
      const storeUrl = cleanAndValidateUrl(row.store_url || row.url || row.storeurl || '');
      
      // Process the store URL to ensure it meets our validation
      const validatedStoreUrl = storeUrl.startsWith('https://www.potterybarnkids.com.sa/en/') ? 
        storeUrl : 
        (storeUrl.includes('potterybarnkids.com.sa/en') ? 
          `https://www.potterybarnkids.com.sa/en/${storeUrl.split('potterybarnkids.com.sa/en')[1]}` : 
          storeUrl);
      
      data[countryCode].push({
        brand: row.brand || 'Unknown Brand',
        title: row.title || 'Special Offer',
        description: row.description || 'Limited time offer',
        discount: row.discount || '10%',
        code: row.code || `CODE${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
        logoImage: row.logoimage || row.logo_image || row.logo || '',
        storeUrl: validatedStoreUrl,
      });
    });
    
    return data;
  };

  const generatePreviewData = (data) => {
    if (!data) return;
    
    const preview = {};
    let totalCoupons = 0;
    
    Object.keys(data).forEach(countryCode => {
      const countryCoupons = data[countryCode];
      const countryObj = countries.find(c => c.code === countryCode);
      
      if (countryObj && countryCoupons.length > 0) {
        preview[countryCode] = {
          name: countryObj.name,
          flag: countryObj.flag,
          couponsCount: countryCoupons.length,
          sampleCoupons: countryCoupons.slice(0, 2) // Show first 2 coupons as sample
        };
        totalCoupons += countryCoupons.length;
      }
    });
    
    setPreviewData({
      countries: Object.keys(preview).length,
      totalCoupons,
      details: preview
    });
  };

  const fetchMissingImagesForData = async (data) => {
    setIsLoading(true);
    setProcessingStatus('Fetching website data...');
    
    const enhancedData = { ...data };
    let processedCount = 0;
    let totalCount = 0;
    
    // Count total coupons
    Object.values(data).forEach(coupons => {
      totalCount += coupons.length;
    });
    
    // Process each country's coupons
    for (const countryCode of Object.keys(data)) {
      const coupons = data[countryCode];
      const enhancedCoupons = [];
      
      // Process each coupon
      for (const coupon of coupons) {
        processedCount++;
        setProcessingStatus(`Fetching data for coupon ${processedCount}/${totalCount}...`);
        
        let finalLogoUrl = coupon.logoImage;
        
        // If we need to fetch from store URL (missing logo)
        if (coupon.storeUrl && !finalLogoUrl) {
          const { logoImage } = await fetchWebsiteData(coupon.storeUrl);
          
          // Only use the fetched logo if we don't already have a valid one
          if (!finalLogoUrl) finalLogoUrl = logoImage;
        }
        
        enhancedCoupons.push({
          ...coupon,
          logoImage: finalLogoUrl
        });
      }
      
      enhancedData[countryCode] = enhancedCoupons;
    }
    
    setImportedData(enhancedData);
    generatePreviewData(enhancedData);
    setProcessingStatus('');
    setIsLoading(false);
    
    return enhancedData;
  };

  const handleImport = async () => {
    if (importedData) {
      setIsLoading(true);
      setProcessingStatus('Enhancing data with website information...');
      
      // First enhance the data with website information
      const enhancedData = await fetchMissingImagesForData(importedData);
      
      // Then import the enhanced data
      setProcessingStatus('Importing enhanced data...');
      await onImport(enhancedData, willOverride);
      
      setProcessingStatus('');
      setIsLoading(false);
    }
  };

  const toggleOverrideMode = () => {
    setWillOverride(!willOverride);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md"
      >
        {showTemplateGenerator ? (
          <ExcelTemplateGenerator
            onBack={() => setShowTemplateGenerator(false)}
            countries={countries}
          />
        ) : (
          <>
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-xl font-bold text-gray-800">Import Coupons</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>
            
            <div className="p-5">
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50'
                    : file
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {file ? (
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                      <SafeIcon
                        icon={isLoading ? FiLoader : FiCheckCircle}
                        className={`text-green-500 text-xl ${isLoading ? 'animate-spin' : ''}`}
                      />
                    </div>
                    <p className="font-medium text-gray-800">{file.name}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {importedData
                        ? `Found coupons for ${Object.keys(importedData).length} countries`
                        : isLoading
                        ? 'Processing file...'
                        : 'No valid data found'}
                    </p>
                    {processingStatus && (
                      <p className="text-sm text-blue-500 mt-2 flex items-center">
                        <SafeIcon icon={FiRefreshCw} className="mr-2 animate-spin" />
                        {processingStatus}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <SafeIcon icon={FiUpload} className="text-gray-500 text-xl" />
                    </div>
                    <p className="font-medium text-gray-800">Drag & drop file here</p>
                    <p className="text-sm text-gray-500 mt-1">or click to browse</p>
                    <button
                      onClick={() => fileInputRef.current.click()}
                      className="mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                    >
                      Select File
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept=".csv,.json,.xlsx,.xls"
                      onChange={handleFileSelect}
                    />
                  </div>
                )}
              </div>
              
              {error && (
                <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center">
                  <SafeIcon icon={FiAlertCircle} className="mr-2 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
              
              {/* Preview Section */}
              {previewData && (
                <div className="mt-4 border border-green-100 bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold text-green-800 mb-2">Data Preview</h3>
                  <p className="text-sm text-green-700 mb-3">
                    Found {previewData.totalCoupons} coupons across {previewData.countries} countries
                  </p>
                  
                  <div className="max-h-40 overflow-auto">
                    {Object.keys(previewData.details).map(countryCode => {
                      const country = previewData.details[countryCode];
                      return (
                        <div key={countryCode} className="mb-3 last:mb-0">
                          <div className="flex items-center mb-1">
                            <span className="mr-1">{country.flag}</span>
                            <span className="text-sm font-medium text-green-800">{country.name}</span>
                            <span className="text-xs bg-green-200 text-green-800 rounded-full px-2 ml-2">
                              {country.couponsCount} coupons
                            </span>
                          </div>
                          
                          {country.sampleCoupons.map((coupon, idx) => (
                            <div key={idx} className="ml-6 mb-1 text-xs text-green-700 flex items-center">
                              <SafeIcon icon={FiFileText} className="mr-1 text-green-600" />
                              {coupon.brand}: {coupon.title} ({coupon.discount})
                              {coupon.storeUrl && (
                                <span className="ml-1 text-xs text-green-600">
                                  - {new URL(coupon.storeUrl).hostname}
                                </span>
                              )}
                            </div>
                          ))}
                          
                          {country.couponsCount > 2 && (
                            <div className="ml-6 text-xs text-green-600">
                              + {country.couponsCount - 2} more...
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Import Mode Toggle */}
              {previewData && (
                <div className="mt-4 flex items-center">
                  <button
                    type="button"
                    onClick={toggleOverrideMode}
                    className={`flex items-center text-sm font-medium p-2 rounded-lg ${
                      willOverride 
                        ? 'bg-red-50 text-red-600' 
                        : 'bg-blue-50 text-blue-600'
                    }`}
                  >
                    <SafeIcon 
                      icon={willOverride ? FiTrash2 : FiUpload} 
                      className="mr-2" 
                    />
                    {willOverride 
                      ? 'Override Mode: Will delete existing coupons' 
                      : 'Append Mode: Will add to existing coupons'}
                  </button>
                </div>
              )}
              
              <div className="mt-6 flex flex-col space-y-3">
                <button
                  onClick={handleImport}
                  disabled={!importedData || isLoading}
                  className={`w-full py-3 rounded-xl font-semibold text-white ${
                    importedData && !isLoading
                      ? willOverride 
                        ? 'bg-red-500 hover:bg-red-600'
                        : 'bg-blue-500 hover:bg-blue-600'
                      : 'bg-gray-300 cursor-not-allowed'
                  } transition-colors flex items-center justify-center`}
                >
                  {isLoading ? (
                    <>
                      <SafeIcon icon={FiLoader} className="animate-spin mr-2" />
                      <span>{processingStatus || 'Processing...'}</span>
                    </>
                  ) : willOverride ? (
                    <>
                      <SafeIcon icon={FiTrash2} className="mr-2" />
                      <span>Replace All Coupons</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiUpload} className="mr-2" />
                      <span>Import Coupons</span>
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => setShowTemplateGenerator(true)}
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 flex items-center justify-center space-x-2 transition-colors disabled:opacity-50"
                >
                  <SafeIcon icon={FiDownload} />
                  <span>Download Template</span>
                </button>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default ExcelImporter;